'use client';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Form } from '@eliadmin/core/src/components/ui/form';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { SparepartsForm } from './spareparts-form';
import { SparepartsModel } from './spareparts-model';
import { Button } from '../ui/button';
import { useState, useEffect } from 'react';
import type { Model, ComponentGroupItem } from '../../api/spareparts-api';
import { useSearchParams, useRouter, usePathname } from 'next/navigation';
import { getRSMotoMotorcycleCategoryConfig } from '../../config/rsmoto-fitments';
import { MotorcycleFitmentFinder } from './motorcycle-fitment-finder';

// Schema for spareparts form validation
export const sparepartsSchema = (VM: FormValidationMessages) => {
  void VM;
  return z.object({
    vin: z.string().optional(),
    model: z.any().optional(), // Model object
    modelName: z.string().optional(), // Display name string
    engine_number: z.string().optional(),
    category: z.enum(['engine', 'frame']).optional(),
    selectedBrand: z.any().optional(), // Brand object
    brandId: z.string().optional(), // Brand ID string
  });
};

export type SparepartsFormValues = z.infer<ReturnType<typeof sparepartsSchema>>;

type ViewMode = 'compatibility' | 'finder' | 'usage-list';

const hasFitmentConfiguration = getRSMotoMotorcycleCategoryConfig().id !== null;
const defaultViewMode: ViewMode = hasFitmentConfiguration ? 'compatibility' : 'finder';

export function SparepartsWrapper() {
  const { t } = useTranslations();
  const { formValidationMessages } = useFormValidationMessages(t);
  const [selectedModel, setSelectedModel] = useState<Model | null>(null);
  const [selectedComponent, setSelectedComponent] = useState<ComponentGroupItem | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(defaultViewMode);

  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  // Initialize view mode from URL params and handle navigation changes
  useEffect(() => {
    const mode = searchParams.get('mode');
    if (mode === 'compatibility' || mode === 'finder' || mode === 'usage-list') {
      setViewMode(mode);
    } else {
      const defaultMode: ViewMode = searchParams.get('modelArticleNumber')
        ? 'finder'
        : defaultViewMode;
      setViewMode(defaultMode);
      const newSearchParams = new URLSearchParams(searchParams.toString());
      newSearchParams.set('mode', defaultMode);
      router.replace(`${pathname}?${newSearchParams.toString()}`);
    }
  }, [searchParams, router, pathname]);

  const form = useForm<SparepartsFormValues>({
    resolver: zodResolver(sparepartsSchema(formValidationMessages)),
    defaultValues: {
      vin: '',
      model: null,
      modelName: '',
      engine_number: '',
      category: 'engine',
      selectedBrand: null,
      brandId: '',
    },
  });

  const handleSubmit = form.handleSubmit(() => {
    // Add your form submission logic here
  });

  const handleViewModeChange = (newMode: ViewMode) => {
    setViewMode(newMode);

    // Update URL with new mode
    const newSearchParams = new URLSearchParams(searchParams.toString());
    newSearchParams.set('mode', newMode);

    // Use push to create proper navigation history for back/forward
    router.push(`${pathname}?${newSearchParams.toString()}`);
  };

  const handleModelChange = (model: Model | null) => {
    setSelectedModel(model);
    setSelectedComponent(null);
    if (model?.brandId) {
      form.setValue('brandId', model.brandId);
      if (form.getValues('selectedBrand')?.id !== model.brandId) {
        form.setValue('selectedBrand', null);
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Mode Selection Buttons */}
      <div className="flex flex-wrap justify-center gap-3">
        <Button
          variant={viewMode === 'compatibility' ? 'default' : 'outline'}
          onClick={() => handleViewModeChange('compatibility')}
          className="min-w-[150px]"
        >
          Doplnky podľa motorky
        </Button>
        <Button
          variant={viewMode === 'finder' ? 'default' : 'outline'}
          onClick={() => handleViewModeChange('finder')}
          className="min-w-[150px]"
        >
          OEM katalóg
        </Button>
        <Button
          variant={viewMode === 'usage-list' ? 'default' : 'outline'}
          onClick={() => handleViewModeChange('usage-list')}
          className="min-w-[150px]"
        >
          Použitie OEM dielu
        </Button>
      </div>

      {/* Conditional Content */}
      {viewMode === 'compatibility' ? (
        <MotorcycleFitmentFinder />
      ) : viewMode === 'finder' ? (
        <Form {...form}>
          <form onSubmit={handleSubmit}>
            <SparepartsForm selectedModel={selectedModel} setSelectedModel={handleModelChange} />
          </form>
          <SparepartsModel
            key={selectedModel?.articleNumber || 'no-model'}
            selectedModel={selectedModel}
            selectedComponent={selectedComponent}
            onComponentSelect={setSelectedComponent}
          />
        </Form>
      ) : (
        <div className="space-y-3 rounded-lg border border-gray-200 p-5" role="status">
          <h2 className="font-semibold">Použitie OEM dielu</h2>
          <p className="text-sm text-gray-600">
            Nové OEM API neposkytuje spätné vyhľadanie motorky podľa dielu. Diely môžete vyhľadať
            výberom značky a modelu v OEM katalógu alebo podľa VIN.
          </p>
          <Button type="button" onClick={() => handleViewModeChange('finder')}>
            Otvoriť OEM katalóg
          </Button>
        </div>
      )}
    </div>
  );
}
